import { Suspense, lazy } from 'react';
import { LoadingSpinner } from '@sharpe/ui/components';
import { BrowserRouter, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { RouterProgress } from '@/components/router-progress';
import { RequireAuth } from '@/components/auth/require-auth';
import { CoreProviders, AuthProviders } from './providers';
import SignInPage from './pages/auth/signin/page';
import SignUpPage from './pages/auth/signup/page';
import AuthErrorPage from './pages/auth/error/page';
import VerifyRequestPage from './pages/auth/verify-request/page';
import AuthCallbackPage from './pages/auth/callback/page';

const HomePage = lazy(() => import('./pages/page'));
const ChatPage = lazy(() => import('./pages/chat/[id]/page'));
const ChatLayout = lazy(() => import('./pages/chat/layout'));
const WorkspacePage = lazy(() => import('./pages/workspace/[id]/page'));
const DataSourcesPage = lazy(() => import('./pages/data/page'));
const DataRouteLayout = lazy(() => import('./pages/data/layout'));
const ManagementPage = lazy(() => import('./pages/management/page'));
const ManagementLayout = lazy(() => import('./pages/management/layout'));
const CliAuthPage = lazy(() => import('./pages/cli/auth/page'));
const InviteAcceptPage = lazy(() => import('./pages/invite/accept/page'));
const PublicChatPage = lazy(() => import('./pages/share/page'));
const DebugConversationPage = lazy(() => import('./pages/debug/conversation/page'));
const DebugToolIconsPage = lazy(() => import('./pages/debug/tool-icons/page'));
const DebugCatalystPage = lazy(() => import('./pages/debug/catalyst/page'));
const DebugTypographyPage = lazy(() => import('./pages/debug/typography/page'));
const DebugHintBarPage = lazy(() => import('./pages/debug/hint-bar/page'));
const DebugIdleDisplayPage = lazy(() => import('./pages/debug/idle-display/page'));
const DebugEmailsPage = lazy(() => import('./pages/debug/emails/page'));
const DebugItemSentencePage = lazy(() => import('./pages/debug/item-sentence/page'));
const DebugToolGalleryPage = lazy(() => import('./pages/debug/tool-gallery/page'));
const DebugCliAuthPage = lazy(() => import('./pages/debug/cli-auth/page'));
const DebugMessageUserPage = lazy(() => import('./pages/debug/message-user/page'));

const FullPageSpinner = (
  <div className="h-screen w-screen flex items-center justify-center">
    <LoadingSpinner size="3xl" className="text-muted-foreground" />
  </div>
);

/**
 * Shared layout for all authenticated routes.
 * Sidebar and auth providers persist across route changes so that
 * navigating (e.g. Home -> Chat) only re-renders the content area.
 */
function AuthenticatedLayout() {
  const location = useLocation();
  // BrowserRouter wraps navigations in startTransition, so if a lazy route
  // suspends (or fails to load), React keeps showing the old UI instead of
  // the fallback. Keying the Suspense boundary by the top-level path segment
  // forces a clean unmount/remount when switching between page types
  // (home <-> chat <-> data <-> management), preventing stale content.
  const routeKey = location.pathname.split('/')[1] || 'home';

  return (
    <RequireAuth>
      <AuthProviders>
        <Suspense
          key={routeKey}
          fallback={
            <div className="flex-1 flex items-center justify-center h-full">
              <LoadingSpinner size="3xl" className="text-muted-foreground" />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </AuthProviders>
    </RequireAuth>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <RouterProgress />
      <CoreProviders>
        <Suspense fallback={FullPageSpinner}>
          <Routes>
            {/* Unauthenticated routes */}
            <Route path="/auth/signin" element={<SignInPage />} />
            <Route path="/auth/signup" element={<SignUpPage />} />
            <Route path="/auth/error" element={<AuthErrorPage />} />
            <Route path="/auth/verify-request" element={<VerifyRequestPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/invite/accept" element={<InviteAcceptPage />} />
            <Route path="/share/:token/chat/:chatId" element={<PublicChatPage />} />

            {/* Debug routes - development only */}
            {!import.meta.env.PROD && (
              <>
                <Route path="/debug/conversation" element={<DebugConversationPage />} />
                <Route path="/debug/tool-icons" element={<DebugToolIconsPage />} />
                <Route path="/debug/catalyst" element={<DebugCatalystPage />} />
                <Route path="/debug/typography" element={<DebugTypographyPage />} />
                <Route path="/debug/hint-bar" element={<DebugHintBarPage />} />
                <Route path="/debug/idle-display" element={<DebugIdleDisplayPage />} />
                <Route path="/debug/emails" element={<DebugEmailsPage />} />
                <Route path="/debug/item-sentence" element={<DebugItemSentencePage />} />
                <Route path="/debug/tool-gallery" element={<DebugToolGalleryPage />} />
                <Route path="/debug/cli-auth" element={<DebugCliAuthPage />} />
                <Route path="/debug/message-user" element={<DebugMessageUserPage />} />
              </>
            )}

            {/* Authenticated routes - shared layout keeps sidebar mounted */}
            <Route element={<AuthenticatedLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route
                path="/chat"
                element={
                  <ChatLayout>
                    <Outlet />
                  </ChatLayout>
                }
              >
                <Route path=":id" element={<ChatPage />} />
              </Route>
              <Route path="/workspace/:id" element={<WorkspacePage />} />
              <Route
                path="/data"
                element={
                  <DataRouteLayout>
                    <DataSourcesPage />
                  </DataRouteLayout>
                }
              />
              <Route
                path="/management"
                element={
                  <ManagementLayout>
                    <ManagementPage />
                  </ManagementLayout>
                }
              />
              <Route path="/cli/auth" element={<CliAuthPage />} />
            </Route>
          </Routes>
        </Suspense>
      </CoreProviders>
    </BrowserRouter>
  );
}
